import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { pepApiRequest, RequestMethod } from 'src/services';
import { WidgetConfig } from './widget';

const transformDataForChart = (apiResults: any[], widget: WidgetConfig) => {
  if (!apiResults || !Array.isArray(apiResults) || apiResults.length === 0) {
    return [];
  }

  const { chartType, chartConfiguration } = widget;
  const { xAxis, yAxis, dataSeries } = chartConfiguration;

  switch (chartType) {
    case 'pie':
      if (dataSeries?.length > 0 && xAxis?.columnName) {
        return apiResults.map((item) => ({
          name: item[xAxis.columnName],
          value: parseFloat(item[dataSeries[0].columnName]) || 0,
        }));
      }
      break;

    case 'radar':
      if (xAxis?.columnName && dataSeries?.length > 0) {
        return apiResults.map((item) => {
          const dataPoint = {
            subject: item[xAxis.columnName],
          };

          dataSeries.forEach((series) => {
            if (series.columnName) {
              dataPoint[series.label || series.columnName] =
                parseFloat(item[series.columnName]) || 0;
            }
          });

          return dataPoint;
        });
      }
      break;

    case 'table': {
      const selectedColumns = [
        xAxis?.columnName,
        yAxis?.columnName,
        ...(dataSeries?.map((series) => series.columnName) || []),
      ].filter(Boolean);

      return apiResults.map((item) => {
        const row = {};
        selectedColumns.forEach((col) => {
          row[col] = item[col];
        });
        return row;
      });
    }

    case 'metric': {
      if (apiResults.length > 0) {
        const latestData = apiResults[0];
        const valueColumn = xAxis?.columnName || dataSeries?.[0]?.columnName;
        const changeColumn = yAxis?.columnName || dataSeries?.[1]?.columnName;

        return {
          value: valueColumn ? parseFloat(latestData[valueColumn]) || 0 : 0,
          change: changeColumn ? parseFloat(latestData[changeColumn]) || 0 : 0,
          unit: dataSeries?.[2]?.columnName
            ? latestData[dataSeries[2].columnName]
            : 'units',
        };
      }
      return {
        value: 0,
        change: 0,
        unit: 'units',
      };
    }

    case 'line':
    case 'bar':
    case 'area':
    case 'scatter':
    default:
      if (xAxis?.columnName && dataSeries?.length > 0) {
        return apiResults.map((item) => {
          const dataPoint = {
            [xAxis.columnName]: item[xAxis.columnName],
          };

          dataSeries.forEach((series) => {
            if (series.columnName) {
              dataPoint[series.columnName] =
                parseFloat(item[series.columnName]) || 0;
            }
          });

          return dataPoint;
        });
      }
      break;
  }

  return apiResults;
};

export const useWidgetData = (widget: WidgetConfig) => {
  const { solutionId } = useParams();
  const [data, setData] = useState<{ data: any; timestamp: number } | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { dataSource } = widget;
      const { type, endpoint, body, method, headers } = dataSource;

      if (type === 'apiInternal') {
        if (!endpoint || !body) {
          setData({ data: null, timestamp: Date.now() });
          setIsLoading(false);
          return;
        }

        const response = await pepApiRequest({
          method: RequestMethod.Post,
          url: `/data/solutions/${solutionId}/tenants/1/executerawkustoquery`,
          params: { query: body },
        });

        let transformedData = response;
        if (response && Array.isArray(response.Results)) {
          const flattenedResults = response.Results.map((item) => {
            if (item.telemetry && typeof item.telemetry === 'object') {
              return {
                ...item,
                ...item.telemetry,
                originalTelemetry: item.telemetry,
              };
            }
            return item;
          }).filter((item) => {
            if (!item || typeof item !== 'object') {
              return false;
            }

            if (item.telemetry === 'telemetry') {
              return false;
            }

            // Keep all other items - let the UI handle missing fields gracefully
            return true;
          });

          const chartData = transformDataForChart(flattenedResults, widget);

          transformedData = {
            ...response,
            Results: flattenedResults,
            ChartData: chartData,
          };
        }

        setData({ data: transformedData, timestamp: Date.now() });
      } else if (type === 'apiExternal') {
        if (!endpoint) {
          setData({ data: null, timestamp: Date.now() });
          setIsLoading(false);
          return;
        }

        let finalEndpoint = endpoint;

        if (solutionId) {
          finalEndpoint = finalEndpoint.replace(':solutionId', solutionId);
        }
        finalEndpoint = finalEndpoint.replace(':tenantId', '1');

        const requestHeaders: Record<string, string> = {};
        headers?.forEach((header) => {
          if (header.key && header.value) {
            requestHeaders[header.key] = header.value;
          }
        });

        const fetchOptions: RequestInit = {
          method,
          headers: requestHeaders,
        };

        if (body && ['POST', 'PUT', 'PATCH'].includes(method)) {
          fetchOptions.body = body;
        }

        const response = await fetch(finalEndpoint, fetchOptions);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const result = await response.json();
        setData({ data: result, timestamp: Date.now() });
      } else if (type === 'adx') {
        let adxConfig = {};
        try {
          adxConfig = body ? JSON.parse(body) : {};
        } catch (e) {
          console.warn('Failed to parse ADX configuration from body:', e);
        }

        setData({
          data: {
            message: 'ADX integration coming soon',
            config: adxConfig,
          },
          timestamp: Date.now(),
        });
      } else {
        setData({ data: null, timestamp: Date.now() });
      }
    } catch (err: any) {
      console.error(`Error fetching API data for widget ${widget.id}:`, err);
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, [
    solutionId,
    widget.id,
    widget.dataSource.type,
    widget.dataSource.endpoint,
    widget.dataSource.method,
    JSON.stringify(widget.dataSource.headers),
    widget.dataSource.body,
  ]);

  useEffect(() => {
    const { dataSource } = widget;
    const { type, endpoint, body } = dataSource;

    const enabled =
      (type === 'apiInternal' && !!body && !!solutionId) ||
      (type === 'apiExternal' && !!endpoint) ||
      type === 'adx';

    if (!enabled) {
      setIsLoading(false);
      setData({ data: null, timestamp: Date.now() });
      return undefined;
    }

    fetchData();
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(fetchData, dataSource.refreshRate * 1000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchData, widget.dataSource.refreshRate]);

  // Manual refetch
  const refetch = fetchData;

  return {
    data,
    isLoading,
    error,
    refetch,
  };
};
